import type { MouseEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Megaphone, Network, Settings, Wallet } from 'lucide-react';
import { formatPriceCompact } from '@/lib/utils/format';
import { firstName } from '@/lib/utils/displayName';
import { faceUrl } from '@/lib/utils/mux';
import type { Face, LastCreator } from '@/lib/types/profile';

export interface ConsoleTab {
  href: string;
  label: string;
  /**
   * The glyph beside the label in the sheet. A scanning aid, never the
   * identifier: nouns like Ambassador and Referrals have no legible icon, so
   * an icon-only row would be a guess. The word says what it is; the glyph
   * makes a list of ten findable at a glance.
   */
  icon: LucideIcon;
  /**
   * A number this section can state about itself, shown at the row's right
   * edge. It is NOT part of the label: as a suffix it truncates with the words
   * and the figures never line up into a column.
   */
  fact?: string;
}

/**
 * Single source of truth for the Studio Console's tabs — a single flat list,
 * NO submenus (decided 2026-08-21; the sub-chip rows were removed the same
 * day they were born). Ambassador is the one listed conditionally — enrolled
 * accounts only. Products has no tab at all — it is edited inline on the
 * owner's storefront. Quizzes has no tab either: a quiz is written from its post
 * on `/agent`, and the page's quiz is picked and edited on the storefront.
 *
 * This list owns the ORDER; the bar only draws it.
 */
const ALL_TABS: ConsoleTab[] = [
  { href: '/dashboard/referrals', label: 'Referrals', icon: Network },
  { href: '/dashboard/ambassador', label: 'Ambassador', icon: Megaphone },
  { href: '/dashboard/wallet', label: 'Wallet', icon: Wallet },
  { href: '/dashboard/settings', label: 'Settings', icon: Settings },
];

/**
 * The sections THIS viewer has — the whole answer to "who sees what", in the
 * file that owns the list. A provider gets the console; a client gets the one
 * page they can reach; a signed-out visitor gets none. The bar reads this and
 * draws it; it does not decide it.
 *
 * `isAmbassador` LISTS the Ambassador section, and that is all it does — the
 * page itself is open to every provider, and typing the URL is a fair way to
 * find out what the programme is. A section nobody has joined yet is clutter in
 * a menu; the page behind it is not a secret.
 *
 * `wallet` gives the Wallet row what is actually in it — the word names a
 * container, the number is a fact about you, and money you can see is the point
 * of earning it. It is the WITHDRAWABLE balance, the same figure the page leads
 * with ("Ready to withdraw"), so the row never promises a number the page then
 * contradicts. Nothing is shown when there is nothing to show — an empty
 * balance, or a blip reading it: a row reading "$0" every day is the opposite
 * of the encouragement this is for.
 */
export function sectionsFor({
  ownPage,
  isAmbassador = false,
  wallet,
}: {
  /** The viewer's own storefront, or null when they have none. The ONLY thing
   *  that decides this list now: a viewer without one has no sections, signed
   *  in or not, so `signedIn` stopped being an input (2026-08-31). */
  ownPage: string | null;
  /** Enrolled ambassador — lists the section, never gates the page. */
  isAmbassador?: boolean;
  /** Spendable balance, or null when there is none to show. */
  wallet?: { amount: number; currency: string } | null;
}): ConsoleTab[] {
  // A client has no sections. Their one page is `/`, which they reach by the
  // logo or by being on it already — a nav row pointing at the page you are
  // standing on is a control that does nothing. Their sheet is not empty
  // though: `SiteBar` still opens it for Sign out.
  if (!ownPage) return [];
  const tabs = isAmbassador
    ? ALL_TABS
    : ALL_TABS.filter((tab) => tab.href !== '/dashboard/ambassador');
  if (!wallet || wallet.amount <= 0) return tabs;
  const fact = formatPriceCompact(wallet);
  return tabs.map((tab) => (tab.href === '/dashboard/wallet' ? { ...tab, fact } : tab));
}

/** Where a pill goes and what it says — not a section, so no glyph. A pill that
 *  leads to a creator's page wears their face, when we hold one. */
export interface Shortcut {
  href: string;
  label: string;
  face: string | null;
}

/** The face's box in the pill; the image is fetched at twice this for a dense screen. */
export const SHORTCUT_FACE_PX = 28;

/**
 * Which homes a viewer has. Deliberately NOT a function of the route: a bar
 * whose contents move as you walk around it has to be re-read at every stop.
 * The bar decides which of them is lit; a signed-out visitor gets Sign in.
 */
export function shortcutsFor({
  ownPage,
  signedIn,
  lastCreator = null,
}: {
  ownPage: string | null;
  signedIn: boolean;
  lastCreator?: LastCreator | null;
}): Shortcut[] {
  if (!signedIn) return [];
  const face = (f: Face | null) => (f ? faceUrl(f, SHORTCUT_FACE_PX * 2) : null);
  // A client's one home is the creator they bought from; one who never bought has none.
  if (!ownPage) {
    return lastCreator
      ? [
          {
            href: `/${lastCreator.username}`,
            label: firstName(lastCreator),
            face: face(lastCreator),
          },
        ]
      : [];
  }
  // Calls is the week until a page of that name exists. Their storefront is "My page" in
  // the sheet. Replies (`/agent`) has no pill yet (2026-10-06): reached from the storefront's
  // quiz doors and by URL while it is tested.
  return [
    { href: '/dashboard', label: 'Calls', face: null },
    { href: '/client', label: 'Clients', face: null },
  ];
}

/**
 * Whether a pill is the page you are on: its own page and its subpages (Clients holds
 * on a client's page), except Calls, whose `/dashboard` would claim every desk page.
 */
export function isShortcutHere(pathname: string, href: string): boolean {
  if (href === '/dashboard') return pathname === href;
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Whether the click lands on the item for the page you are already on.
 * Stricter than `isShortcutHere` on purpose: a tab still holds on its subpages,
 * but from a subpage the section's row must still
 * navigate.
 */
export function isNavItemReselect(pathname: string, href: string): boolean {
  return pathname === href;
}

/**
 * Where a page begins — `PageFrame`'s own top edge (`pt-beat`), so a scrolled-to
 * heading rests exactly where an unscrolled one does. Retyped because Tailwind's
 * tokens are not readable from JS; if `beat` moves, move this with it.
 */
const PAGE_TOP = 112;

/**
 * Scroll the page so `[data-page-anchor]` sits where a page's heading sits.
 * Manual math, NOT scrollIntoView: the root layout puts a global
 * scroll-pt-header (80px, clearance for the public pages' fixed header) on
 * html, which scrollIntoView would add on top of any offset here. The
 * floating bar sits at the top on every width (the capsule rail on lg+, the
 * corner burger below), and `PAGE_TOP` clears it everywhere.
 *
 * Returns false when the page has no anchor.
 */
export function scrollToPageAnchor(behavior: ScrollBehavior): boolean {
  // One anchor per page. When several elements carry it the first in document
  // order wins — which is how the dashboard feed lets its heading over Today
  // outrank the Today panel sitting just below it.
  const anchor = document.querySelector('[data-page-anchor]');
  if (!anchor) return false;
  const offset = PAGE_TOP;
  window.scrollTo({
    top: window.scrollY + anchor.getBoundingClientRect().top - offset,
    behavior,
  });
  return true;
}

/**
 * Clicking the nav item for the page you're already on: scroll back to that page's
 * `[data-page-anchor]`, or to the top. Next's same-URL push can't do this — it doesn't
 * remount the page, so a landing effect (the dashboard feed lands on its anchor day) never re-runs.
 *
 * Only ever call this when `isNavItemReselect` is true — it preventDefaults.
 */
export function handleNavReselect(e: MouseEvent<HTMLAnchorElement>): void {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  if (!scrollToPageAnchor('smooth')) window.scrollTo({ top: 0, behavior: 'smooth' });
}
